import React, { FC, Fragment, useCallback, useMemo } from "react";
import Icon from "components/Icon/Icon";
import { ButtonType } from "enums/Button";
import { IconType } from "enums/Icon";
import { FormikProps, getIn } from "formik";
import { useTranslation } from "react-i18next";
import { ActionMeta } from "react-select";
import { createFieldBlurHandler } from "utils/formValidation";
import { AddButton } from "../../../../components/Button";
import Input from "../../../../components/Input/Input";
import Select from "../../../../components/Select/Select";
import { FieldWrapper } from "../../../../styles/formStyles";
import {
  buildInboundAddressOptions,
  emptyInboundPackage,
  InboundShipmentFormValues,
} from "./inboundShipmentFormHelper";
import {
  AddAddressWrapper,
  AddPackageWrapper,
  PackageHeader,
  PackageRemoveButton,
  PackageTitle,
} from "./InboundShipmentPanel.styles";

interface InboundShipmentFormProps
  extends FormikProps<InboundShipmentFormValues> {
  addresses: any;
  isAddressesLoading: boolean;
  units: string;
  isLabeled: boolean;
  // enableReinitialize would wipe anything typed before the record arrives,
  // so the fields stay locked while the detail fetch is in flight.
  isDetailLoading: boolean;
  onAddAddress: () => void;
}

const InboundShipmentForm: FC<InboundShipmentFormProps> = (props) => {
  const {
    setFieldValue,
    setTouched,
    values,
    touched,
    errors,
    handleChange,
    addresses,
    isAddressesLoading,
    units,
    isLabeled,
    isDetailLoading,
    onAddAddress,
  } = props;
  const handleBlur = createFieldBlurHandler(props);
  const { t } = useTranslation("common");
  const isFieldDisabled = isLabeled || isDetailLoading;

  const weightUnit = t(`units.${units}.primaryShort`);
  const sizeUnit = t(`units.${units}.secondaryShort`);

  const addressOptions = useMemo(
    () => buildInboundAddressOptions(addresses),
    [addresses],
  );

  const handleChangeSelect = useCallback(
    (selectedOption: unknown, context: ActionMeta<unknown>) => {
      if (context.name) {
        setFieldValue(context.name, selectedOption);
      }
    },
    [setFieldValue],
  );

  const handleAddPackage = useCallback(() => {
    setFieldValue("packages", [...values.packages, emptyInboundPackage()]);
  }, [setFieldValue, values.packages]);

  const handleRemovePackage = (index: number) => {
    setFieldValue(
      "packages",
      values.packages.filter((_, i) => i !== index),
    );
    // Touched flags are positional — drop the removed row's entry so error
    // highlights don't slide onto the next package.
    if (Array.isArray(touched.packages)) {
      setTouched(
        {
          ...touched,
          packages: touched.packages.filter((_, i) => i !== index),
        },
        false,
      );
    }
  };

  const packageField = (index: number, field: string) =>
    `packages[${index}].${field}`;

  const dimensionInput = (
    index: number,
    field: "weight" | "height" | "length" | "width",
    unit: string,
  ) => {
    const name = packageField(index, field);
    return (
      <Input
        placeholder={t(`inbound.form.${field}`)}
        name={name}
        type="number"
        min="0.01"
        step="any"
        value={values.packages[index][field]}
        disabled={isFieldDisabled}
        onChange={handleChange}
        onBlur={handleBlur}
        tooltip={getIn(errors, name)}
        invalid={getIn(errors, name) && getIn(touched, name)}
        endAdornment={<span>{unit}</span>}
      />
    );
  };

  return (
    <div>
      <FieldWrapper>
        <Select
          placeholder={t("inbound.form.address")}
          name="address"
          value={values.address}
          onChange={handleChangeSelect}
          options={addressOptions}
          isLoading={isAddressesLoading}
          disabled={isFieldDisabled}
          onBlur={handleBlur}
          tooltip={errors.address as string}
          invalid={!!errors.address && !!touched.address}
        />
      </FieldWrapper>
      {!isLabeled && (
        <AddAddressWrapper>
          <AddButton type={ButtonType.Button} onClick={onAddAddress}>
            {t("inbound.addAddress")}
          </AddButton>
        </AddAddressWrapper>
      )}
      {values.packages.map((pkg, index) => (
        <Fragment key={index}>
          {values.packages.length > 1 && (
            <PackageHeader>
              <PackageTitle>
                {t("inbound.form.packageTitle", { number: index + 1 })}
              </PackageTitle>
              {!isLabeled && (
                <PackageRemoveButton
                  aria-label={t("inbound.form.removePackage")}
                  title={t("inbound.form.removePackage")}
                  disabled={isDetailLoading}
                  onClick={() => handleRemovePackage(index)}
                >
                  <Icon type={IconType.Delete} />
                </PackageRemoveButton>
              )}
            </PackageHeader>
          )}
          <FieldWrapper>
            {dimensionInput(index, "weight", weightUnit)}
            {dimensionInput(index, "height", sizeUnit)}
          </FieldWrapper>
          <FieldWrapper>
            {dimensionInput(index, "length", sizeUnit)}
            {dimensionInput(index, "width", sizeUnit)}
          </FieldWrapper>
          <FieldWrapper>
            <Input
              placeholder={t("inbound.form.comment")}
              name={packageField(index, "customerComment")}
              value={pkg.customerComment}
              disabled={isFieldDisabled}
              onChange={handleChange}
              onBlur={handleBlur}
              tooltip={getIn(errors, packageField(index, "customerComment"))}
              invalid={
                getIn(errors, packageField(index, "customerComment")) &&
                getIn(touched, packageField(index, "customerComment"))
              }
            />
          </FieldWrapper>
        </Fragment>
      ))}
      {!isLabeled && (
        <AddPackageWrapper>
          <AddButton
            type={ButtonType.Button}
            disabled={isDetailLoading}
            onClick={handleAddPackage}
          >
            {t("inbound.form.addPackage")}
          </AddButton>
        </AddPackageWrapper>
      )}
    </div>
  );
};

export default React.memo(InboundShipmentForm);
